import { useState } from 'react';
import { useAprovarAutorizacao, useReprovarAutorizacao } from './queries/useAutorizacaoMutations';
import { useAuth } from '@/contexts/AuthContext';
import type { Demanda, User, DemandaAutorizacao } from '@/models';

interface UseDemandAutorizacaoParams {
  demanda: Demanda;
  currentUser: User | null;
  onSuccess?: () => void;
}

export const useDemandAutorizacao = ({ 
  demanda, 
  onSuccess 
}: UseDemandAutorizacaoParams) => {
  const [showAutorizacaoModal, setShowAutorizacaoModal] = useState(false);
  const [autorizacaoAction, setAutorizacaoAction] = useState<'aprovar' | 'reprovar' | null>(null);
  const [comentario, setComentario] = useState('');

  const { user } = useAuth();

  // Mutations
  const aprovarMutation = useAprovarAutorizacao();
  const reprovarMutation = useReprovarAutorizacao();

  // ✅ NOVA LÓGICA: Verificar se o usuário pertence ao setor que precisa autorizar
  // Backend valida: usuarios_setores.usuario_fk = currentUser.id AND usuarios_setores.setor_fk = autorizador_fk
  const autorizacaoPendente = demanda.demanda_autorizacao?.find(
    (aut: DemandaAutorizacao) => {
      const isStatusAguardando = aut.status === 'AGUARDANDO';
      
      // Verificar se o setor logado do usuário é o autorizador desta autorização
      const pertenceAoSetorAutorizador = aut.autorizador_fk === user?.setor_logado_fk;
      
      return isStatusAguardando && pertenceAoSetorAutorizador;
    }
  );

  // Permissões - só pode autorizar se:
  // 1. Tem uma autorização pendente para o SETOR logado (autorizador_fk === setor_logado_fk)
  // 2. A autorização está em status AGUARDANDO
  // 3. A demanda está em status AGUARDANDOAUTORIZACAO
  // O backend vai validar se o usuário realmente pertence ao setor (usuarios_setores)
  const canAutorizar = Boolean(
    autorizacaoPendente && 
    demanda.status === 'AGUARDANDOAUTORIZACAO' &&
    user?.setor_logado_fk
  );

  const handleAprovarClick = () => {
    setAutorizacaoAction('aprovar');
    setShowAutorizacaoModal(true);
  };

  const handleReprovarClick = () => {
    setAutorizacaoAction('reprovar');
    setShowAutorizacaoModal(true);
  };

  const handleConfirmAutorizacao = async () => {
    if (!autorizacaoPendente || !demanda.id) return;

    try {
      if (autorizacaoAction === 'aprovar') {
        await aprovarMutation.mutateAsync({
          demandaAutorizacaoId: autorizacaoPendente.id,
          comentario: comentario,
          demandaId: demanda.id,
        });
      } else if (autorizacaoAction === 'reprovar') {
        await reprovarMutation.mutateAsync({
          demandaAutorizacaoId: autorizacaoPendente.id,
          comentario: comentario,
          demandaId: demanda.id,
        });
      }

      // Resetar estado e fechar modal
      setShowAutorizacaoModal(false);
      setAutorizacaoAction(null);
      setComentario('');

      // Callback de sucesso
      onSuccess?.();
    } catch (error) {
      // O erro já é tratado nas mutations com toast
      console.error('Erro ao processar autorização:', error);
    }
  };

  const handleCancelAutorizacao = () => {
    setShowAutorizacaoModal(false);
    setAutorizacaoAction(null);
    setComentario('');
  };

  return {
    // Estado
    canAutorizar,
    autorizacaoPendente,
    showAutorizacaoModal,
    autorizacaoAction,
    comentario,
    setComentario,
    
    // Ações
    handleAprovarClick,
    handleReprovarClick,
    handleConfirmAutorizacao,
    handleCancelAutorizacao,
    
    // Status das mutations
    isAprovando: aprovarMutation.isPending,
    isReprovando: reprovarMutation.isPending,
    isProcessing: aprovarMutation.isPending || reprovarMutation.isPending,
  };
};